import React, { useEffect, useRef, useState } from 'react'

interface BasinFormEmbedProps {
  src: string
  id: string
}

export const BasinFormEmbed: React.FC<BasinFormEmbedProps> = ({ src, id }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.action === 'setHeight' && iframeRef.current) {
        iframeRef.current.style.height = `${event.data.height}px`
      } else if (event.data.action === 'redirect') {
        window.location.href = event.data.url
      }
    }

    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  const [iframeHeight, setIframeHeight] = useState(750) // Set default height to 300px

  useEffect(() => {
    const iframe = iframeRef.current

    if (iframe) {
      // Set up the interval to request height
      const intervalId = setInterval(() => {
        iframe.contentWindow?.postMessage('getHeight', '*')
      }, 500)

      // Event listener to handle receiving the height
      const handleMessage = event => {
        // Ensure the message is from the expected origin, for security reasons
        if (event.origin !== 'your_iframe_origin') return

        // Check for height data in the message and clear the interval once received
        if (event.data && event.data.height) {
          clearInterval(intervalId)
          setIframeHeight(event.data.height) // Set iframe height to received value
        }
      }

      // Attach the message event listener
      window.addEventListener('message', handleMessage)

      // Cleanup on unmount or when iframe changes
      return () => {
        clearInterval(intervalId)
        window.removeEventListener('message', handleMessage)
      }
    }
  }, [])

  return (
    <div className='w-full'>
      <iframe
        id={id}
        ref={iframeRef}
        src={src}
        allowTransparency
        frameBorder='0'
        style={{
          border: 'none',
          overflow: 'hidden',
          width: '100%',
          height: iframeHeight
        }}
        width='100%'
        aria-label='Form content'
      ></iframe>
    </div>
  )
}

// Form listeners
interface BasinEventListenerProps {
  formId?: string
  onFormReady?: () => void
  onSubmitSuccess?: () => void
  onSubmitError?: () => void
}

export const BasinEventListener: React.FC<BasinEventListenerProps> = ({
  formId,
  onFormReady,
  onSubmitSuccess,
  onSubmitError
}) => {
  useEffect(() => {
    const handleBasinEvents = (event: MessageEvent) => {
      try {
        if (event.origin !== 'https://usebasin.com') return
        if (!event.data) {
          console.debug('[BasinEventListener] Received message without data')
          return
        }

        const { action } = event.data
        // Trigger callbacks based on Basin events
        if (action === 'basinFormReady' && onFormReady) {
          onFormReady()
        } else if (action === 'basinSubmitDone' && onSubmitSuccess) {
          onSubmitSuccess()
        } else if (action === 'basinSubmitError' && onSubmitError) {
          onSubmitError()
        } else {
          console.debug('[BasinEventListener] Unhandled action:', action)
        }
      } catch (error) {
        console.error('[BasinEventListener] Error processing message:', error)
        onSubmitError?.()
      }
    }
    window.addEventListener('message', handleBasinEvents)
    return () => {
      window.removeEventListener('message', handleBasinEvents)
    }
  }, [onFormReady, onSubmitSuccess, onSubmitError])

  return null // This component does not render anything
}
